import React, { useMemo } from 'react'
import TableWrapper from './table-wrapper'
import TableBody from './table-body'
import TableHeader from './table-header'
import { AdvancedGridProps, DataType, GridContextConfig } from './types/advanced-grid.types'
import { GridContext } from './context'
import {
    useDataProvider,
    useGridFilter,
    useSelectedRecords,
    useSorter,
    useActionsConfig
} from './hooks'

const AdvancedGrid: React.FC<AdvancedGridProps & GridContextConfig> = ({
    actions,
    actionResolvers,
    advancedSearch,
    allowSelect,
    bordered,
    buttons,
    columns,
    dataProcessor,
    filters,
    filterOptions,
    hover,
    id,
    maxPages,
    recordsPerPageValues,
    rowControl,
    rowControlOptions,
    rowDataProcessor,
    sorts,
    striped,
    title,
    url
}) => {
    const [sortValues, handleChangeSort] = useSorter(sorts)
    const [filterValues, handleChangeFilter] = useGridFilter()
    const {
        completed,
        data,
        loading,
        total,
        pageSize,
        currentPage,
        pages,
        handlePageSizeChange,
        handleChangeCurrentPage,
        handleNextPage,
        handlePreviousPage
    } = useDataProvider<DataType[]>({ url, dataProcessor, sorts: sortValues })
    const actionsConfig = useActionsConfig(actions, actionResolvers)
    const {
        clearSelection,
        selectAllCheck,
        resetCheckAll,
        selectedRecords,
        handleSelectAll,
        handleSelectRecord
    } = useSelectedRecords(data || [])
    /**
     * This variables holds the column keys, used to extract the content from the data
     */
    const columnKeys = useMemo(
        () => (columns ? columns.map((col) => (typeof col === 'object' ? col.key : col)) : []),
        [columns]
    )

    return (
        <GridContext.Provider
            value={{
                actions: actionsConfig,
                actionResolvers,
                advancedSearch,
                clearSelection,
                columns,
                columnKeys,
                buttons,
                config: {
                    allowSelect,
                    bordered,
                    hover,
                    striped
                },
                currentPage,
                dataLoaded: completed,
                filters,
                filterOptions,
                filterValues,
                gridId: id,
                data,
                events: {
                    handleSelectRecord,
                    handleSelectAll,
                    handleChangeFilter,
                    handleChangeSort,
                    handlePageSizeChange,
                    handleChangeCurrentPage,
                    handleNextPage,
                    handlePreviousPage
                },
                loading,
                maxPages,
                pages,
                pageSize,
                resetCheckAll,
                recordsPerPageValues,
                rowControl,
                rowControlOptions,
                rowDataProcessor,
                selectAllCheck,
                selectedRecords,
                sorts: sortValues,
                title,
                totalRecords: total
            }}
        >
            <TableWrapper>
                <TableHeader />
                <TableBody />
            </TableWrapper>
        </GridContext.Provider>
    )
}

AdvancedGrid.defaultProps = {
    allowSelect: true,
    bordered: true,
    hover: false,
    maxPages: 10,
    recordsPerPageValues: [10, 20, 50, 100],
    striped: true
}

export default React.memo(AdvancedGrid)
